import { captureMessageWithTags } from 'src/utils/sentry';
import { atlassianStore as store } from 'src/utils/store';

/*
In this file, we store metadata into local storage as shown below:

"recently-viewed-projects:{user_uuid}:{workspace_uuid}": ["{project_uuid_1}", "{project_uuid_2}", ...]
*/

type User = { uuid: string };
type Workspace = { uuid: string };

export const MAX_RECENT_PROJECTS = 5;

const storageName = (user: User, workspace: Workspace) =>
  `recently-viewed-projects:${user.uuid}:${workspace.uuid}`;

type GetType = {
  user?: User;
  workspace?: Workspace;
};

export function getRecentProjects({ user, workspace }: GetType): Array<string> {
  if (!store.enabled) {
    return [];
  }
  if (!user || !workspace) {
    return [];
  }
  return store.get(storageName(user, workspace)) || [];
}

type AddType = {
  user: User;
  workspace: Workspace;
  projectKey: string;
};

export function addRecentProject({ user, workspace, projectKey }: AddType) {
  if (!store.enabled) {
    return;
  }
  if (!user || !workspace || !projectKey) {
    const message = `Invalid parameters to addRecentProject: ${
      !user ? 'no user' : ''
    } ${!workspace ? 'no workspace' : ''} ${!projectKey ? 'no project' : ''}`;
    captureMessageWithTags(message);
    // eslint-disable-next-line no-console
    console.error(message);
    return;
  }

  const currentProjects = getRecentProjects({ user, workspace });
  let nextProjects: Array<string> = [];
  if (currentProjects.length === 0) {
    nextProjects = [projectKey];
  }
  const existingIndex = currentProjects.indexOf(projectKey);
  if (existingIndex === -1) {
    // add to the front
    nextProjects = [projectKey, ...currentProjects];
  } else if (existingIndex === 0) {
    // already the most recent project
    nextProjects = currentProjects;
  } else if (existingIndex > 0) {
    // move to the front
    nextProjects = [
      projectKey,
      ...currentProjects.slice(0, existingIndex),
      ...currentProjects.slice(existingIndex + 1),
    ];
  }

  store.setStrictlyNecessaryItem(
    storageName(user, workspace),
    nextProjects.slice(0, MAX_RECENT_PROJECTS)
  );
}
